import { postStreaming } from './streamingApiService';

type LanguageCode = 'en' | 'zh' | 'zh-HK' | 'zh-TW';
type StateSetter = (updater: string | ((prev: string) => string)) => void;

/**
 * AI translation service using streaming.
 * @param text The text to translate.
 * @param targetLanguage The target language code.
 * @param sourceLanguage The source language code.
 * @param setTargetState React state setter for the target text field (e.g., setZhDescription).
 * @param onStreamComplete Callback for when streaming is complete.
 * @param onStreamError Callback for when a streaming error occurs.
 * @returns Resolves when the streaming setup is complete, errors handled by onStreamError.
 */
export const translateTextStream = async (
  text: string,
  targetLanguage: LanguageCode,
  sourceLanguage: LanguageCode,
  setTargetState: StateSetter,
  onStreamComplete?: () => void,
  onStreamError?: (error: Error) => void
): Promise<void> => {
  console.log(`AI TRANSLATE (Streaming): Translating "${text}" from ${sourceLanguage} to ${targetLanguage}`);

  setTargetState('');

  const endpoint = '/translate/ai-translate-stream';
  const payload: Record<string, unknown> = {
    text: text,
    targetLanguage,
    sourceLanguage,
  };

  try {
    await postStreaming(
      endpoint,
      payload,
      (chunk: string) => {
        setTargetState((prevText: string) => prevText + chunk);
      },
      () => {
        console.log('Streaming finished successfully.');
        if (onStreamComplete) onStreamComplete();
      },
      (error: Error) => {
        console.error('Streaming error in aiTranslateService:', error);
        if (onStreamError) onStreamError(error);
      }
    );
  } catch (error: unknown) {
    console.error('Failed to initiate streaming request:', error);
    if (onStreamError) onStreamError(error as Error);
  }
};

const aiTranslateService = {
  translateTextStream,
};

export default aiTranslateService;
